"use client";

// CopyDisc — THE copy control (John, 2026-10-25: "use our new copy button
// everywhere that we can. on hover makes more sense").
//
// The link bar's round disc (components/skills/PasteUrl PasteCopy), as one
// piece any surface can seat beside what it lets a person take: a pale wash of
// the organism's hue, a hairline, the mark in ink, settling to a check when it
// lands. The word lives in the label for a screen reader. Where the disc sits
// inside something it copies (a code block, a key, a line), that surface brings
// it up on hover or focus (`.copy-disc` in globals.css; always shown where
// nothing hovers). A clipboard that refuses leaves the words on screen, which
// is the fallback.

import { useCallback, useEffect, useRef, useState } from "react";

function Mark({ done }: { done: boolean }) {
  return done ? (
    <svg width="15" height="15" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path d="M3 7.3 5.8 10 11 4.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden>
      <rect x="8" y="8" width="12" height="12" rx="2.5" />
      <path d="M16 8V5.5A1.5 1.5 0 0 0 14.5 4h-9A1.5 1.5 0 0 0 4 5.5v9A1.5 1.5 0 0 0 5.5 16H8" />
    </svg>
  );
}

export default function CopyDisc({
  text,
  label = "copy",
  className,
  onCopied,
}: {
  /** exactly what lands on the clipboard */
  text: string;
  /** what a screen reader hears before it is pressed ("copy", "copy the key", "copy link") */
  label?: string;
  className?: string;
  onCopied?: () => void;
}) {
  const [done, setDone] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      return;
    }
    setDone(true);
    onCopied?.();
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setDone(false), 1800);
  }, [text, onCopied]);

  return (
    <button
      type="button"
      className={`copy-disc${className ? ` ${className}` : ""}`}
      data-done={done || undefined}
      onClick={() => void copy()}
      aria-label={done ? "copied" : label}
    >
      <Mark done={done} />
      <span className="copy-disc__label" aria-live="polite">{done ? "copied" : label}</span>
    </button>
  );
}
